import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Place as PlaceIcon,
  CalendarMonth as CalendarIcon,
  FilterAlt as FilterIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { PathfinderDataPoint, CoTravelerResult } from '../../services/pathfinderApi';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';

// Filter type for chart interactions
export interface ChartFilter {
  type: 'hour' | 'day' | 'dayHour' | 'location';
  day?: string; // 'Sun', 'Mon', etc.
  hour?: number; // 0-23
  location?: string;
}

interface InteractiveChartsProps {
  dataPoints: PathfinderDataPoint[];
  dwellLocations: any[];
  coTravelerResults: CoTravelerResult[];
  studentColors: Record<string, string>;
  isDarkMode?: boolean;
  activeFilter?: ChartFilter | null;
  onFilterChange?: (filter: ChartFilter | null) => void;
}

const InteractiveCharts: React.FC<InteractiveChartsProps> = ({
  dataPoints,
  dwellLocations,
  coTravelerResults,
  studentColors: _studentColors,
  isDarkMode = true,
  activeFilter,
  onFilterChange,
}) => {
  const { singular } = useParticipantLabels();
  const theme = useTheme();
  const [activeView, setActiveView] = useState<'activity' | 'locations'>('activity');

  // Reverse-geocoded labels for dwell rows where the DB has no place_name /
  // place_address (geocode_location_id not linked). cop-dwells returns nulls
  // in that case; the chart used to show only "37.xxx, -121.xxx".
  const [dwellGeocodeLabels, setDwellGeocodeLabels] = useState<Record<string, string>>({});

  const dwellGeocodeSignature = useMemo(
    () =>
      dwellLocations
        .map((d: any) => `${d.id ?? ''}:${d.latitude ?? ''}:${d.longitude ?? ''}`)
        .join('|'),
    [dwellLocations],
  );

  useEffect(() => {
    setDwellGeocodeLabels({});
    const token = process.env.REACT_APP_MAPBOX_TOKEN || (typeof window !== 'undefined' ? (window as any).MAPBOX_TOKEN : '');
    if (!token || !dwellLocations.length) return;

    const keys: string[] = [];
    const seen = new Set<string>();
    for (const d of dwellLocations) {
      const named = ((d as any).place_name || '').trim() || ((d as any).place_address || '').trim();
      if (named) continue;
      const lat = (d as any).latitude;
      const lon = (d as any).longitude;
      if (lat == null || lon == null) continue;
      const key = `${Number(lat).toFixed(4)},${Number(lon).toFixed(4)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }

    const toFetch = keys.slice(0, 28);
    if (toFetch.length === 0) return;

    let cancelled = false;
    (async () => {
      const batch: Record<string, string> = {};
      for (const key of toFetch) {
        if (cancelled) return;
        const [latStr, lonStr] = key.split(',');
        const lat = parseFloat(latStr);
        const lon = parseFloat(lonStr);
        try {
          const url =
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json` +
            `?access_token=${token}&types=poi,address,place&limit=1`;
          const res = await fetch(url);
          if (!res.ok) continue;
          const data = await res.json();
          const feat = data.features?.[0];
          const label = feat?.place_name || feat?.text;
          if (label && typeof label === 'string') batch[key] = label;
        } catch {
          /* ignore single-point failures */
        }
        await new Promise((r) => setTimeout(r, 60));
      }
      if (!cancelled && Object.keys(batch).length > 0) {
        setDwellGeocodeLabels((prev) => ({ ...prev, ...batch }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dwellGeocodeSignature]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed by dwellGeocodeSignature

  // Theme-aware color palette - uses theme colors but keeps vibrant data viz colors
  const colors = useMemo(() => ({
    // Primary accent - uses theme primary or cyan for dark mode
    primary: theme.palette.primary.main,
    primaryLight: theme.palette.primary.light,
    primaryDark: theme.palette.primary.dark,
    
    // Secondary colors for data
    success: theme.palette.success?.main || '#4caf50',
    warning: theme.palette.warning?.main || '#ff9800',
    error: theme.palette.error?.main || '#f44336',
    info: theme.palette.info?.main || '#2196f3',
    
    // UI colors from theme
    background: theme.palette.background.paper,
    backgroundAlt: isDarkMode ? '#1a1a1a' : theme.palette.grey[50],
    border: isDarkMode ? '#333333' : theme.palette.divider,
    text: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    textMuted: isDarkMode ? '#666' : theme.palette.grey[500],
    
    // Chart-specific colors that stay vibrant
    heatmapLow: isDarkMode ? '#1a1a1a' : theme.palette.grey[100],
    heatmapHigh: theme.palette.primary.main,
    weekend: theme.palette.warning?.main || '#ff9800',
    
    // Filter/selection colors
    selected: theme.palette.error?.main || '#ff4444',
    selectedLight: theme.palette.error?.light || '#ff8888',
    selectedBorder: alpha(theme.palette.error?.main || '#ff4444', 0.6),
  }), [theme, isDarkMode]);

  // Helper to check if a filter is active for a specific element
  const isFilterActive = useCallback((type: ChartFilter['type'], day?: string, hour?: number, location?: string) => {
    if (!activeFilter) return false;
    if (activeFilter.type !== type) return false;
    if (type === 'dayHour') return activeFilter.day === day && activeFilter.hour === hour;
    if (type === 'day') return activeFilter.day === day;
    if (type === 'hour') return activeFilter.hour === hour;
    if (type === 'location') return activeFilter.location === location;
    return false;
  }, [activeFilter]);

  // Helper to check if a day is highlighted by the current filter (for weekly chart)
  const isDayHighlighted = useCallback((day: string) => {
    if (!activeFilter) return false;
    if (activeFilter.type === 'day') return activeFilter.day === day;
    if (activeFilter.type === 'dayHour') return activeFilter.day === day;
    return false;
  }, [activeFilter]);

  // Helper to check if an hour is highlighted by the current filter (for rhythm chart)
  const isHourHighlighted = useCallback((hour: number) => {
    if (!activeFilter) return false;
    if (activeFilter.type === 'hour') return activeFilter.hour === hour;
    if (activeFilter.type === 'dayHour') return activeFilter.hour === hour;
    return false;
  }, [activeFilter]);

  // Handle clicking on a chart element to toggle filter
  const handleFilterClick = useCallback((filter: ChartFilter) => {
    if (!onFilterChange) return;
    
    // If same filter is active, clear it
    if (activeFilter && 
        activeFilter.type === filter.type &&
        activeFilter.day === filter.day &&
        activeFilter.hour === filter.hour &&
        activeFilter.location === filter.location) {
      onFilterChange(null);
    } else {
      onFilterChange(filter);
    }
  }, [activeFilter, onFilterChange]);

  // Get filter description for display
  const getFilterDescription = useCallback((filter: ChartFilter) => {
    const days: Record<string, string> = {
      'Sun': 'Sunday', 'Mon': 'Monday', 'Tue': 'Tuesday', 
      'Wed': 'Wednesday', 'Thu': 'Thursday', 'Fri': 'Friday', 'Sat': 'Saturday'
    };
    
    if (filter.type === 'dayHour') {
      return `${days[filter.day!]} at ${filter.hour!.toString().padStart(2, '0')}:00`;
    }
    if (filter.type === 'day') {
      return days[filter.day!];
    }
    if (filter.type === 'hour') {
      return `${filter.hour!.toString().padStart(2, '0')}:00 (all days)`;
    }
    if (filter.type === 'location') {
      return filter.location!.length > 30 ? filter.location!.substring(0, 28) + '...' : filter.location!;
    }
    return '';
  }, []);

  // Custom scrollbar styles
  const scrollbarSx = {
    '&::-webkit-scrollbar': { width: '6px', height: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)',
      borderRadius: '3px',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: isDarkMode ? 'rgba(255, 255, 255, 0.3) transparent' : 'rgba(0, 0, 0, 0.25) transparent',
  };

  // Build encounter-based timestamps for digital co-travelers that have no
  // GPS track data in `dataPoints`. Wi-Fi / Bluetooth / Cellular co-travelers
  // only surface as encounter rows on a CoTravelerResult. Without this they
  // would be invisible in every time-based chart below.
  // (Mirrors overwatch_rf reference implementation.)
  const encounterTimestamps = useMemo(() => {
    if (!coTravelerResults.length) return [] as { timestamp: Date; entityType: string }[];

    const trackedIds = new Set<string>();
    dataPoints.forEach(p => {
      if ((p as any).isCoTraveler) {
        trackedIds.add((p.studentId || '').toLowerCase());
        trackedIds.add((p.deviceId || '').toLowerCase());
      }
    });

    const out: { timestamp: Date; entityType: string }[] = [];
    coTravelerResults.forEach(result => {
      const deviceLower = (result.coTravelerDevice || '').toLowerCase();
      const entityPart = deviceLower.includes(' - ') ? deviceLower.split(' - ')[0] : deviceLower;
      const alreadyHasTrack = trackedIds.has(deviceLower) || trackedIds.has(entityPart);
      if (alreadyHasTrack || !result.encounters?.length) return;
      const entityType = (result.coTravelerEntityType || 'Unknown').toUpperCase();
      result.encounters.forEach(enc => {
        out.push({ timestamp: enc.timestamp as any, entityType });
      });
    });
    return out;
  }, [dataPoints, coTravelerResults]);

  // Process data for activity heatmap (hour x day of week)
  const activityHeatmap = useMemo(() => {
    if (!dataPoints.length && !encounterTimestamps.length) return [];
    
    const heatmap: Record<string, Record<number, number>> = {
      'Sun': {}, 'Mon': {}, 'Tue': {}, 'Wed': {}, 'Thu': {}, 'Fri': {}, 'Sat': {}
    };
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Initialize all hours
    days.forEach(day => {
      for (let h = 0; h < 24; h++) heatmap[day][h] = 0;
    });
    
    dataPoints.forEach(point => {
      const date = new Date(point.timestamp);
      const day = days[date.getDay()];
      const hour = date.getHours();
      heatmap[day][hour]++;
    });

    encounterTimestamps.forEach(enc => {
      const date = new Date(enc.timestamp);
      const day = days[date.getDay()];
      const hour = date.getHours();
      heatmap[day][hour]++;
    });
    
    // Convert to array format for visualization
    return days.map(day => ({
      day,
      ...Object.fromEntries(
        Array.from({ length: 24 }, (_, h) => [h.toString(), heatmap[day][h]])
      )
    }));
  }, [dataPoints, encounterTimestamps]);

  // Get max count for heatmap coloring
  const maxHeatmapCount = useMemo(() => {
    if (!activityHeatmap.length) return 1;
    let max = 0;
    activityHeatmap.forEach(row => {
      for (let h = 0; h < 24; h++) {
        const val = (row as any)[h.toString()] || 0;
        if (val > max) max = val;
      }
    });
    return max || 1;
  }, [activityHeatmap]);

  // Process data for daily rhythm (activity by hour across all days).
  // Separates primary student activity from co-traveler activity (SRP / RP /
  // digital CT). `ctActivity` aggregates non-SRP/RP co-travelers — both GPS-
  // tracked physical co-travelers that aren't SRP/RP, and the synthesized
  // encounter timestamps for Wi-Fi / Bluetooth / Cellular digital co-travelers.
  const dailyRhythm = useMemo(() => {
    if (!dataPoints.length && !encounterTimestamps.length) return [];
    
    const hourlyData: { hour: string; activity: number; srpActivity: number; rpActivity: number; ctActivity: number }[] = [];
    const hourCounts: Record<number, { primary: number; srp: number; rp: number; ct: number }> = {};
    
    for (let h = 0; h < 24; h++) hourCounts[h] = { primary: 0, srp: 0, rp: 0, ct: 0 };
    
    dataPoints.forEach(point => {
      const hour = new Date(point.timestamp).getHours();
      const isCoTraveler = (point as any).isCoTraveler;
      
      if (isCoTraveler) {
        const studentId = (point.studentId || '').toUpperCase();
        if (studentId.startsWith('SRP')) {
          hourCounts[hour].srp++;
        } else if (studentId.startsWith('RP')) {
          hourCounts[hour].rp++;
        } else {
          hourCounts[hour].ct++;
        }
      } else {
        hourCounts[hour].primary++;
      }
    });

    encounterTimestamps.forEach(enc => {
      const hour = new Date(enc.timestamp).getHours();
      if (enc.entityType === 'SRP') {
        hourCounts[hour].srp++;
      } else if (enc.entityType === 'RP') {
        hourCounts[hour].rp++;
      } else {
        hourCounts[hour].ct++;
      }
    });
    
    for (let h = 0; h < 24; h++) {
      hourlyData.push({
        hour: `${h.toString().padStart(2, '0')}:00`,
        activity: hourCounts[h].primary,
        srpActivity: hourCounts[h].srp,
        rpActivity: hourCounts[h].rp,
        ctActivity: hourCounts[h].ct,
      });
    }
    
    return hourlyData;
  }, [dataPoints, encounterTimestamps]);

  // Process top locations for radar chart
  const topLocations = useMemo(() => {
    if (!dwellLocations.length) return [];

    const coordKey = (dwell: any) => {
      if (dwell.latitude == null || dwell.longitude == null) return 'unknown';
      return `${Number(dwell.latitude).toFixed(4)},${Number(dwell.longitude).toFixed(4)}`;
    };

    const displayFor = (dwell: any) => {
      const pn = (dwell.place_name || '').trim();
      const pa = (dwell.place_address || '').trim();
      if (pn) return pn;
      if (pa) return pa;
      const ck = coordKey(dwell);
      const geo = dwellGeocodeLabels[ck];
      if (geo) return geo;
      if (dwell.latitude != null && dwell.longitude != null) {
        return `${Number(dwell.latitude).toFixed(3)}, ${Number(dwell.longitude).toFixed(3)}`;
      }
      return 'Unknown';
    };

    const aggKey = (dwell: any) => {
      const pn = (dwell.place_name || '').trim();
      const pa = (dwell.place_address || '').trim();
      if (pn) return `n:${pn}`;
      if (pa) return `a:${pa}`;
      return `c:${coordKey(dwell)}`;
    };

    const locationCounts: Record<string, { count: number; duration: number; display: string }> = {};

    dwellLocations.forEach((dwell: any) => {
      const key = aggKey(dwell);
      if (!locationCounts[key]) {
        locationCounts[key] = { count: 0, duration: 0, display: '' };
      }
      locationCounts[key].display = displayFor(dwell);
      locationCounts[key].count += 1;

      const start = parseBackendDateTime(dwell.start_time);
      const end = parseBackendDateTime(dwell.end_time);
      if (start && end) {
        locationCounts[key].duration += (end.getTime() - start.getTime()) / (1000 * 60); // minutes
      }
    });

    return Object.entries(locationCounts)
      .map(([, data]) => {
        const loc = data.display;
        return {
          location: loc.length > 28 ? `${loc.substring(0, 26)}…` : loc,
          fullLocation: loc,
          visits: data.count,
          duration: Math.round(data.duration),
        };
      })
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 25);
  }, [dwellLocations, dwellGeocodeLabels]);

  // Weekly activity summary - separates primary from co-traveler activity.
  // Like dailyRhythm, ctActivity folds in both GPS-tracked non-SRP/RP co-
  // travelers and synthesized digital co-traveler encounter timestamps.
  const weeklyActivity = useMemo(() => {
    if (!dataPoints.length && !encounterTimestamps.length) return [];
    
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayCounts: Record<string, { primary: number; srp: number; rp: number; ct: number }> = {};
    days.forEach(d => dayCounts[d] = { primary: 0, srp: 0, rp: 0, ct: 0 });
    
    dataPoints.forEach(point => {
      const day = days[new Date(point.timestamp).getDay()];
      const isCoTraveler = (point as any).isCoTraveler;
      
      if (isCoTraveler) {
        const studentId = (point.studentId || '').toUpperCase();
        if (studentId.startsWith('SRP')) {
          dayCounts[day].srp++;
        } else if (studentId.startsWith('RP')) {
          dayCounts[day].rp++;
        } else {
          dayCounts[day].ct++;
        }
      } else {
        dayCounts[day].primary++;
      }
    });

    encounterTimestamps.forEach(enc => {
      const day = days[new Date(enc.timestamp).getDay()];
      if (enc.entityType === 'SRP') {
        dayCounts[day].srp++;
      } else if (enc.entityType === 'RP') {
        dayCounts[day].rp++;
      } else {
        dayCounts[day].ct++;
      }
    });
    
    return days.map(day => ({
      day,
      activity: dayCounts[day].primary,
      srpActivity: dayCounts[day].srp,
      rpActivity: dayCounts[day].rp,
      ctActivity: dayCounts[day].ct,
    }));
  }, [dataPoints, encounterTimestamps]);

  // Calculate summary statistics - now includes co-traveler breakdown
  const stats = useMemo(() => {
    const uniqueDevices = new Set(dataPoints.map(p => p.studentId));
    
    // Separate primary points from co-traveler points.
    // ctPoints counts non-SRP/RP co-travelers: physical GPS-tracked ones plus
    // synthesized digital-encounter timestamps (so the Co-Traveler series in
    // the charts/legend reflects Wi-Fi / BT / Cellular activity too).
    let primaryPoints = 0;
    let srpPoints = 0;
    let rpPoints = 0;
    let ctPoints = 0;
    
    dataPoints.forEach(p => {
      const isCoTraveler = (p as any).isCoTraveler;
      if (isCoTraveler) {
        const studentId = (p.studentId || '').toUpperCase();
        if (studentId.startsWith('SRP')) {
          srpPoints++;
        } else if (studentId.startsWith('RP')) {
          rpPoints++;
        } else {
          ctPoints++;
        }
      } else {
        primaryPoints++;
      }
    });

    encounterTimestamps.forEach(enc => {
      if (enc.entityType === 'SRP') {
        srpPoints++;
      } else if (enc.entityType === 'RP') {
        rpPoints++;
      } else {
        ctPoints++;
      }
    });
    
    // Calculate time range without spread operator (avoids stack overflow on large datasets)
    let minTime = Infinity;
    let maxTime = -Infinity;
    dataPoints.forEach(p => {
      const time = new Date(p.timestamp).getTime();
      if (time < minTime) minTime = time;
      if (time > maxTime) maxTime = time;
    });
    const timeRange = dataPoints.length > 0 ? {
      start: new Date(minTime),
      end: new Date(maxTime),
    } : null;

    // Find peak activity hour
    const hourCounts: Record<number, number> = {};
    dataPoints.forEach(p => {
      const h = new Date(p.timestamp).getHours();
      hourCounts[h] = (hourCounts[h] || 0) + 1;
    });
    const peakHour = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];

    return {
      totalPoints: dataPoints.length,
      primaryPoints,
      srpPoints,
      rpPoints,
      ctPoints,
      uniqueDevices: uniqueDevices.size,
      totalDwells: dwellLocations.length,
      totalCoTravelers: coTravelerResults.length,
      timeRange,
      peakHour: peakHour ? parseInt(peakHour[0]) : null,
      avgPointsPerDay: timeRange 
        ? Math.round(dataPoints.length / Math.max(1, Math.ceil((timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60 * 60 * 24))))
        : 0,
    };
  }, [dataPoints, dwellLocations, coTravelerResults, encounterTimestamps]);

  const getHeatmapColor = useCallback((value: number) => {
    if (value === 0) return colors.heatmapLow;
    const intensity = Math.min(value / maxHeatmapCount, 1);
    // Use theme primary color with varying opacity/intensity
    return alpha(colors.heatmapHigh, 0.2 + intensity * 0.8);
  }, [colors.heatmapLow, colors.heatmapHigh, maxHeatmapCount]);

  if (stats.totalPoints === 0 && stats.totalDwells === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <TimelineIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1, opacity: 0.5 }} />
        <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
          No Pattern of Life Data
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
          Load device data to analyze behavioral patterns
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      overflow: 'auto', 
      p: 1,
      bgcolor: theme.palette.background.default,
      ...scrollbarSx
    }}>
      {/* Stats Summary Row */}
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        mb: 1, 
        flexWrap: 'wrap',
        p: 0.5,
        borderRadius: 1,
        bgcolor: colors.backgroundAlt,
        border: `1px solid ${colors.border}`,
      }}>
        <Box sx={{ flex: 1, minWidth: 50, textAlign: 'center' }}>
          <Typography sx={{ 
            fontSize: '0.85rem', 
            fontWeight: 'bold', 
            color: colors.primary,
            lineHeight: 1.1
          }}>
            {stats.primaryPoints.toLocaleString()}
          </Typography>
          <Typography sx={{ fontSize: '0.5rem', color: colors.textSecondary }}>
            Points
          </Typography>
        </Box>
        {stats.srpPoints > 0 && (
          <Box sx={{ flex: 1, minWidth: 50, textAlign: 'center' }}>
            <Typography sx={{ 
              fontSize: '0.85rem', 
              fontWeight: 'bold', 
              color: '#f44336',
              lineHeight: 1.1
            }}>
              {stats.srpPoints.toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: '0.5rem', color: colors.textSecondary }}>
              SRP
            </Typography>
          </Box>
        )}
        {stats.rpPoints > 0 && (
          <Box sx={{ flex: 1, minWidth: 50, textAlign: 'center' }}>
            <Typography sx={{ 
              fontSize: '0.85rem', 
              fontWeight: 'bold', 
              color: '#ff9800',
              lineHeight: 1.1
            }}>
              {stats.rpPoints.toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: '0.5rem', color: colors.textSecondary }}>
              RP
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 50, textAlign: 'center' }}>
          <Typography sx={{ 
            fontSize: '0.85rem', 
            fontWeight: 'bold', 
            color: colors.success,
            lineHeight: 1.1
          }}>
            {stats.totalDwells}
          </Typography>
          <Typography sx={{ fontSize: '0.5rem', color: colors.textSecondary }}>
            Dwells
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 50, textAlign: 'center' }}>
          <Typography sx={{ 
            fontSize: '0.85rem', 
            fontWeight: 'bold', 
            color: colors.warning,
            lineHeight: 1.1
          }}>
            {stats.peakHour !== null ? `${stats.peakHour}:00` : '--'}
          </Typography>
          <Typography sx={{ fontSize: '0.5rem', color: colors.textSecondary }}>
            Peak
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 50, textAlign: 'center' }}>
          <Typography sx={{ 
            fontSize: '0.85rem', 
            fontWeight: 'bold', 
            color: colors.info,
            lineHeight: 1.1
          }}>
            {stats.avgPointsPerDay}
          </Typography>
          <Typography sx={{ fontSize: '0.5rem', color: colors.textSecondary }}>
            Avg/Day
          </Typography>
        </Box>
      </Box>

      {/* View Toggle */}
      <Box sx={{ mb: 1 }}>
        <ToggleButtonGroup
          value={activeView}
          exclusive
          onChange={(_, v) => v && setActiveView(v)}
          size="small"
          sx={{
            width: '100%',
            '& .MuiToggleButton-root': {
              flex: 1,
              fontSize: '0.6rem',
              py: 0.3,
              color: colors.textSecondary,
              borderColor: colors.border,
              '&.Mui-selected': {
                bgcolor: alpha(colors.primary, 0.15),
                color: colors.primary,
                borderColor: colors.primary,
              }
            }
          }}
        >
          <ToggleButton value="activity">
            <CalendarIcon sx={{ fontSize: '0.8rem', mr: 0.3 }} />
            Activity
          </ToggleButton>
          <ToggleButton value="locations">
            <PlaceIcon sx={{ fontSize: '0.8rem', mr: 0.3 }} />
            Locations
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Active Filter Indicator */}
      {activeFilter && onFilterChange && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1,
          p: 0.5,
          px: 1,
          borderRadius: 1,
          bgcolor: alpha(colors.selected, 0.15),
          border: `1px solid ${alpha(colors.selected, 0.4)}`,
        }}>
          <FilterIcon sx={{ fontSize: '0.75rem', color: colors.selected }} />
          <Typography sx={{ 
            flex: 1, 
            fontSize: '0.6rem', 
            color: colors.selectedLight,
            fontWeight: 500
          }}>
            Filtering: {getFilterDescription(activeFilter)}
          </Typography>
          <Tooltip title="Clear filter">
            <IconButton 
              size="small" 
              onClick={() => onFilterChange(null)}
              sx={{ 
                p: 0.25,
                color: colors.selectedLight,
                '&:hover': { bgcolor: alpha(colors.selected, 0.2) }
              }}
            >
              <CloseIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Activity View - Combined Heatmap, Rhythm, and Weekly */}
      {activeView === 'activity' && (
        <>
        {/* Heatmap Section */}
        {activityHeatmap.length > 0 && (
        <Box sx={{ 
          p: 1, 
          borderRadius: 1.5, 
          bgcolor: colors.backgroundAlt, 
          border: `1px solid ${colors.border}`,
          mb: 1
        }}>
          {/* Title row with legend */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box>
              <Typography sx={{ 
                fontSize: '0.75rem', 
                fontWeight: 'bold', 
                color: colors.primary
              }}>
                Activity Heatmap
              </Typography>
              <Typography sx={{ fontSize: '0.55rem', color: colors.textMuted }}>
                Hour × Day of Week
              </Typography>
            </Box>
            {/* Legend - moved to upper right */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography sx={{ fontSize: '0.45rem', color: colors.textMuted }}>Less</Typography>
              {[0, 0.33, 0.66, 1].map((intensity, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.3,
                    bgcolor: getHeatmapColor(intensity * maxHeatmapCount)
                  }}
                />
              ))}
              <Typography sx={{ fontSize: '0.45rem', color: colors.textMuted }}>More</Typography>
            </Box>
          </Box>
          
          {/* Hour labels */}
          <Box sx={{ display: 'flex', ml: '32px', mb: 0.25 }}>
            {[0, 3, 6, 9, 12, 15, 18, 21].map(h => {
              const isHourMatch = isHourHighlighted(h);
              return (
              <Box key={h} sx={{ flex: '0 0 12.5%', textAlign: 'center' }}>
                <Typography sx={{ 
                  fontSize: '0.45rem', 
                  color: isHourMatch ? colors.selectedLight : colors.textMuted,
                  fontWeight: isHourMatch ? 'bold' : 'normal'
                }}>
                  {h.toString().padStart(2, '0')}
                </Typography>
              </Box>
            );
            })}
          </Box>
          
          {/* Heatmap grid */}
          {activityHeatmap.map((row, _rowIdx) => {
            const isRowHighlighted = isDayHighlighted(row.day);
            return (
            <Box key={row.day} sx={{ display: 'flex', alignItems: 'center', mb: '2px' }}>
              <Typography sx={{ 
                width: 28, 
                fontSize: '0.5rem', 
                color: isRowHighlighted ? colors.selectedLight : colors.textSecondary,
                fontWeight: isRowHighlighted ? 'bold' : 'normal',
                textAlign: 'right',
                pr: 0.5
              }}>
                {row.day}
              </Typography>
              <Box sx={{ display: 'flex', flex: 1, gap: '1px' }}>
                {Array.from({ length: 24 }, (_, h) => {
                  const value = (row as any)[h.toString()] || 0;
                  const isSelected = isFilterActive('dayHour', row.day, h);
                  const isDayMatch = isDayHighlighted(row.day);
                  const isHourMatch = isHourHighlighted(h);
                  const isCrossHighlighted = !isSelected && (isDayMatch || isHourMatch);
                  
                  return (
                    <Tooltip 
                      key={h} 
                      title={`${row.day} ${h.toString().padStart(2, '0')}:00 - ${value} points${onFilterChange && value > 0 ? ' (click to filter)' : value === 0 ? '' : ''}`}
                      arrow
                    >
                      <Box 
                        onClick={() => {
                          // Only allow filtering on cells with data (value > 0)
                          if (value > 0) {
                            handleFilterClick({ type: 'dayHour', day: row.day, hour: h });
                          }
                        }}
                        sx={{
                          flex: 1,
                          height: 14,
                          bgcolor: isSelected ? colors.selected : isCrossHighlighted ? colors.selectedLight : getHeatmapColor(value),
                          borderRadius: 0.3,
                          cursor: onFilterChange && value > 0 ? 'pointer' : 'default',
                          transition: 'all 0.15s',
                          border: isSelected ? `2px solid ${colors.selectedLight}` : isCrossHighlighted ? `1px solid ${colors.selectedLight}` : 'none',
                          boxShadow: isSelected ? `0 0 10px ${colors.selectedBorder}` : 'none',
                          opacity: isCrossHighlighted ? 0.85 : 1,
                          '&:hover': onFilterChange && value > 0 ? {
                            transform: 'scale(1.15)',
                            zIndex: 1,
                            boxShadow: `0 0 6px ${alpha(colors.primary, 0.5)}`,
                          } : {}
                        }} 
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          );
          })}
        </Box>
        )}

        {/* Daily Rhythm Chart - shows hourly activity */}
        {dailyRhythm.length > 0 && (
        <Box sx={{ 
          p: 1, 
          borderRadius: 1.5, 
          bgcolor: colors.backgroundAlt, 
          border: `1px solid ${colors.border}`,
          mb: 1
        }}>
          <Typography sx={{ 
            fontSize: '0.7rem', 
            fontWeight: 'bold', 
            color: colors.primary, 
            mb: 0.25 
          }}>
            Hourly Activity {onFilterChange && <Typography component="span" sx={{ fontSize: '0.5rem', color: colors.textMuted, fontWeight: 'normal' }}>(click to filter)</Typography>}
          </Typography>
          
          {/* Legend for co-travelers */}
          {(stats.srpPoints > 0 || stats.rpPoints > 0 || stats.ctPoints > 0) && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, bgcolor: colors.primary, borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>{singular}</Typography>
              </Box>
              {stats.srpPoints > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: '#f44336', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>SRP ({stats.srpPoints.toLocaleString()})</Typography>
                </Box>
              )}
              {stats.rpPoints > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: '#ff9800', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>RP ({stats.rpPoints.toLocaleString()})</Typography>
                </Box>
              )}
              {stats.ctPoints > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: '#ef5350', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>Co-Traveler ({stats.ctPoints.toLocaleString()})</Typography>
                </Box>
              )}
            </Box>
          )}
          
          <ResponsiveContainer width="100%" height={150}>
            <BarChart 
              data={dailyRhythm}
              onClick={(data) => {
                if (data?.activePayload?.[0] && onFilterChange) {
                  const payload = data.activePayload[0].payload;
                  const totalCount = (payload.activity || 0) + (payload.srpActivity || 0) + (payload.rpActivity || 0) + (payload.ctActivity || 0);
                  // Only filter if there's actual data
                  if (totalCount > 0) {
                    const hour = parseInt(payload.hour.split(':')[0]);
                    handleFilterClick({ type: 'hour', hour });
                  }
                }
              }}
            >
              <defs>
                <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.primary} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={colors.primary} stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="srpGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f44336" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#f44336" stopOpacity={0.4}/>
                </linearGradient>
                <linearGradient id="rpGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff9800" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#ff9800" stopOpacity={0.4}/>
                </linearGradient>
                <linearGradient id="ctGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef5350" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#ef5350" stopOpacity={0.4}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis 
                dataKey="hour" 
                tick={{ fill: colors.textSecondary, fontSize: 8 }}
                interval={2}
              />
              <YAxis 
                tick={{ fill: colors.textSecondary, fontSize: 8 }}
                width={30}
              />
              <RechartsTooltip 
                contentStyle={{ 
                  background: colors.backgroundAlt, 
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: '0.75rem'
                }}
                cursor={onFilterChange ? { fill: alpha(colors.primary, 0.1) } : false}
                formatter={(value: number, name: string) => {
                  const label = name === 'activity' ? singular : name === 'srpActivity' ? 'SRP' : name === 'rpActivity' ? 'RP' : 'Co-Traveler';
                  return [value.toLocaleString(), label];
                }}
              />
              <Bar 
                dataKey="activity" 
                stackId="stack"
                fill="url(#activityGradient)" 
                name={singular}
                cursor={onFilterChange ? 'pointer' : 'default'}
              >
                {dailyRhythm.map((entry, index) => {
                  const hour = parseInt(entry.hour.split(':')[0]);
                  const isSelected = isFilterActive('hour', undefined, hour);
                  const isHighlighted = isHourHighlighted(hour);
                  return (
                    <Cell 
                      key={index} 
                      fill={isSelected ? colors.selected : isHighlighted ? colors.selectedLight : colors.primary}
                      stroke={isSelected ? colors.selectedLight : isHighlighted ? colors.selectedLight : 'none'}
                      strokeWidth={isSelected || isHighlighted ? 2 : 0}
                      opacity={isHighlighted && !isSelected ? 0.7 : 1}
                    />
                  );
                })}
              </Bar>
              {stats.srpPoints > 0 && (
                <Bar 
                  dataKey="srpActivity" 
                  stackId="stack"
                  fill="url(#srpGradient)" 
                  name="SRP"
                  cursor={onFilterChange ? 'pointer' : 'default'}
                />
              )}
              {stats.rpPoints > 0 && (
                <Bar 
                  dataKey="rpActivity" 
                  stackId="stack"
                  fill="url(#rpGradient)" 
                  name="RP"
                  cursor={onFilterChange ? 'pointer' : 'default'}
                />
              )}
              {stats.ctPoints > 0 && (
                <Bar
                  dataKey="ctActivity"
                  stackId="stack"
                  fill="url(#ctGradient)"
                  name="Co-Traveler"
                  cursor={onFilterChange ? 'pointer' : 'default'}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
        )}

        {/* Weekly Pattern Chart - shows daily activity with co-traveler breakdown */}
        {weeklyActivity.length > 0 && (
        <Box sx={{ 
          p: 1, 
          borderRadius: 1.5, 
          bgcolor: colors.backgroundAlt, 
          border: `1px solid ${colors.border}`
        }}>
          <Typography sx={{ 
            fontSize: '0.7rem', 
            fontWeight: 'bold', 
            color: colors.primary, 
            mb: 0.25 
          }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><BarChartIcon sx={{ fontSize: 14 }} /> Weekly Pattern</Box> {onFilterChange && <Typography component="span" sx={{ fontSize: '0.5rem', color: colors.textMuted, fontWeight: 'normal' }}>(click to filter)</Typography>}
          </Typography>

          {/* Legend for co-travelers */}
          {(stats.srpPoints > 0 || stats.rpPoints > 0 || stats.ctPoints > 0) && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, bgcolor: colors.primary, borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>{singular}</Typography>
              </Box>
              {stats.srpPoints > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: '#f44336', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>SRP</Typography>
                </Box>
              )}
              {stats.rpPoints > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: '#ff9800', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>RP</Typography>
                </Box>
              )}
              {stats.ctPoints > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: '#ef5350', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: '0.55rem', color: colors.textSecondary }}>Co-Traveler</Typography>
                </Box>
              )}
            </Box>
          )}

          <ResponsiveContainer width="100%" height={150}>
            <BarChart 
              data={weeklyActivity}
              onClick={(data) => {
                if (data?.activePayload?.[0] && onFilterChange) {
                  const payload = data.activePayload[0].payload;
                  const totalCount = (payload.activity || 0) + (payload.srpActivity || 0) + (payload.rpActivity || 0) + (payload.ctActivity || 0);
                  // Only filter if there's actual data
                  if (totalCount > 0) {
                    const day = payload.day;
                    handleFilterClick({ type: 'day', day });
                  }
                }
              }}
            >
              <defs>
                <linearGradient id="weeklyGradient2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.primary} stopOpacity={0.9}/>
                  <stop offset="95%" stopColor={colors.primaryDark} stopOpacity={0.6}/>
                </linearGradient>
                <linearGradient id="weeklySrpGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f44336" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#c62828" stopOpacity={0.6}/>
                </linearGradient>
                <linearGradient id="weeklyRpGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff9800" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#e65100" stopOpacity={0.6}/>
                </linearGradient>
                <linearGradient id="weeklyCtGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef5350" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#c62828" stopOpacity={0.6}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis 
                dataKey="day" 
                tick={{ fill: colors.textSecondary, fontSize: 9 }}
              />
              <YAxis 
                tick={{ fill: colors.textSecondary, fontSize: 8 }}
                width={30}
              />
              <RechartsTooltip 
                contentStyle={{ 
                  background: colors.backgroundAlt, 
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: '0.75rem'
                }}
                cursor={onFilterChange ? { fill: alpha(colors.primary, 0.1) } : false}
                formatter={(value: number, name: string) => {
                  const label = name === 'activity' ? singular : name === 'srpActivity' ? 'SRP' : name === 'rpActivity' ? 'RP' : 'Co-Traveler';
                  return [value.toLocaleString(), label];
                }}
              />
              <Bar 
                dataKey="activity" 
                stackId="weekStack"
                fill="url(#weeklyGradient2)" 
                name={singular}
                cursor={onFilterChange ? 'pointer' : 'default'}
              >
                {weeklyActivity.map((entry, index) => {
                  const isWeekend = entry.day === 'Sat' || entry.day === 'Sun';
                  const isSelected = isFilterActive('day', entry.day);
                  const isHighlighted = isDayHighlighted(entry.day);
                  
                  if (isSelected) {
                    return (
                      <Cell 
                        key={index} 
                        fill={colors.selected}
                        stroke={colors.selectedLight}
                        strokeWidth={2}
                      />
                    );
                  }
                  if (isHighlighted) {
                    return (
                      <Cell 
                        key={index} 
                        fill={colors.selectedLight}
                        stroke={colors.selectedLight}
                        strokeWidth={2}
                        opacity={0.7}
                      />
                    );
                  }
                  return (
                    <Cell 
                      key={index} 
                      fill={isWeekend ? colors.weekend : colors.primary}
                      opacity={isWeekend ? 0.7 : 0.9}
                    />
                  );
                })}
              </Bar>
              {stats.srpPoints > 0 && (
                <Bar 
                  dataKey="srpActivity" 
                  stackId="weekStack"
                  fill="url(#weeklySrpGradient)" 
                  name="SRP"
                  cursor={onFilterChange ? 'pointer' : 'default'}
                />
              )}
              {stats.rpPoints > 0 && (
                <Bar 
                  dataKey="rpActivity" 
                  stackId="weekStack"
                  fill="url(#weeklyRpGradient)" 
                  name="RP"
                  cursor={onFilterChange ? 'pointer' : 'default'}
                />
              )}
              {stats.ctPoints > 0 && (
                <Bar
                  dataKey="ctActivity"
                  stackId="weekStack"
                  fill="url(#weeklyCtGradient)"
                  name="Co-Traveler"
                  cursor={onFilterChange ? 'pointer' : 'default'}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
        )}
        </>
      )}

      {/* Top Locations View */}
      {activeView === 'locations' && topLocations.length > 0 && (
        <Box sx={{ 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: colors.backgroundAlt, 
          border: `1px solid ${colors.border}` 
        }}>
          <Typography sx={{ 
            fontSize: '0.8rem', 
            fontWeight: 'bold', 
            color: colors.primary, 
            mb: 0.5 
          }}>
          Dwell Locations
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted, mb: 1 }}>
            Most visited places by time spent{onFilterChange ? ' (click to filter)' : ''}
          </Typography>
          
          <ResponsiveContainer width="100%" height={500}>
            <BarChart 
              data={topLocations} 
              layout="vertical"
              onClick={(data) => {
                if (data?.activePayload?.[0] && onFilterChange) {
                  const payload = data.activePayload[0].payload;
                  // Only filter if there's actual data (duration > 0)
                  if (payload.totalDuration > 0) {
                    const location = payload.fullLocation;
                    handleFilterClick({ type: 'location', location });
                  }
                }
              }}
            >
              <defs>
                <linearGradient id="locationGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="5%" stopColor={colors.primary} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={colors.primaryDark} stopOpacity={0.8}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis 
                type="number" 
                tick={{ fill: colors.textSecondary, fontSize: 9 }}
                tickFormatter={(v) => `${Math.round(v)}m`}
              />
              <YAxis 
                type="category" 
                dataKey="location" 
                width={100}
                tick={{ fill: colors.textSecondary, fontSize: 9 }}
              />
              <RechartsTooltip 
                contentStyle={{ 
                  background: colors.backgroundAlt, 
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: '0.75rem'
                }}
                formatter={(value: any, name: string) => [
                  name === 'duration' ? `${value} min` : `${value} visits`,
                  name === 'duration' ? 'Time Spent' : 'Visits'
                ]}
                cursor={onFilterChange ? { fill: alpha(colors.primary, 0.1) } : false}
              />
              <Bar 
                dataKey="duration" 
                fill="url(#locationGradient)" 
                radius={[0, 4, 4, 0]}
                name="Duration"
                cursor={onFilterChange ? 'pointer' : 'default'}
              >
                {topLocations.map((loc, index) => {
                  const isSelected = isFilterActive('location', undefined, undefined, loc.fullLocation);
                  return (
                    <Cell 
                      key={index} 
                      fill={isSelected ? colors.selected : colors.primary}
                      stroke={isSelected ? colors.selectedLight : 'none'}
                      strokeWidth={isSelected ? 2 : 0}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}


      {/* No Data States */}
      {activeView === 'locations' && topLocations.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <PlaceIcon sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
          <Typography sx={{ fontSize: '0.8rem', color: colors.textMuted }}>
            No dwell locations available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InteractiveCharts;
